import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toShortNumFormat } from '@common/lib/toShortNumFormat';
import { type ShortStorageItemProps } from '@common/types/components';
import { ResourceComponent } from '@components/helpers/ObjectsText';
import { StringSmall, StringXSmall } from '@components/Typography/Typography';
import { ResourceImage } from '@components/ui/ResourceImage/ResourceImage';
import { Scale } from '@components/ui/Scale/Scale';

import { convertRemToPixels } from '@/common/lib/convertRemtoPx';

import css from './styles.module.scss';

import lockSvg from '/img/icons/lock-icon.svg';

interface Props extends ShortStorageItemProps {
  bonus?: number;
}

const ShortStorageItem: FC<Props> = ({ kind, storage, bonus }) => {
  const { t } = useTranslation();
  const percentage = storage ? ((storage.stock || 0) * 100) / storage.limit : 0;
  const bonusStock =
    storage && bonus ? Math.round(storage.stock * (bonus / 100)) : 0;
  const bonusPercentage =
    storage && storage.limit ? (bonusStock * 100) / storage?.limit : 0;
  const resource = ResourceComponent(t);

  return (
    <div className={css.storageItem}>
      <StringXSmall weight="regular" className="text-white/60 max-w-[66px]">
        {resource[kind].nameMaterial}
      </StringXSmall>
      {storage ? (
        <div className="w-14 flex flex-col gap-1">
          <StringSmall weight="bold">
            {toShortNumFormat(storage.stock || 0)}
          </StringSmall>
          {!!bonus && !!bonusStock && (
            <StringXSmall weight="bold" className="!text-gold">
              +{toShortNumFormat(bonusStock || 0)}
            </StringXSmall>
          )}
          <Scale
            className="!overflow-visible"
            height={convertRemToPixels(0.25)}
            percentage={percentage}
            scaleClassName={bonus ? 'rounded-r-none' : undefined}
            additionalTrack={
              bonus
                ? {
                    percentage: bonusPercentage,
                    className: 'bg-gold rounded-r-lg',
                    style: { left: `${percentage}%` },
                  }
                : undefined
            }
          />
        </div>
      ) : (
        <img src={lockSvg} className="w-4 h-4" alt="lock" />
      )}
      <ResourceImage
        kind={kind}
        direction={kind === 'wood' ? 'left' : 'right'}
        className="absolute right-0 top-1/2 -translate-y-1/2"
        size={convertRemToPixels(4.5)}
      />
    </div>
  );
};

export { ShortStorageItem };
